import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Bell, BellOff, X, Check, CheckCheck, Trash2, ExternalLink, Zap,
} from "lucide-react";
import {
  isPushSupported, currentPushPermission,
  subscribeToPush, unsubscribeFromPush, isCurrentlySubscribed,
  sendTestPush,
} from "@/lib/web-push";

const POLL_INTERVAL_MS = 30_000;

function formatRelativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d}j`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unread | message | chantier | litige
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const timerRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!isPushSupported()) return;
    isCurrentlySubscribed().then(setPushEnabled);
  }, [user]);

  // Handle service worker "NAVIGATE" messages (click on push notification)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (e) => {
      if (e.data && e.data.type === "NAVIGATE" && e.data.link) {
        nav(e.data.link);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [nav]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success("Notifications push désactivées.");
      } else {
        await subscribeToPush();
        setPushEnabled(true);
        toast.success("Notifications push activées !");
        await sendTestPush();
      }
    } catch (err) {
      toast.error(err?.message || "Impossible d'activer les notifications push.");
    } finally {
      setPushBusy(false);
    }
  };

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await apiClient.get("/notifications", { params: { limit: 30 } });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    timerRef.current = setInterval(fetchNotifs, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [user, fetchNotifs]);

  // Close drawer when clicking outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markOneRead = async (id) => {
    try {
      await apiClient.post("/notifications/read", { ids: [id] });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((n) => Math.max(0, n - 1));
    } catch {}
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await apiClient.post("/notifications/read", { ids: null });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {} finally {
      setLoading(false);
    }
  };

  const removeNotification = async (id) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={drawerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 border-2 border-[#0A0A0B] bg-white flex items-center justify-center hover:bg-[#FF4D00] hover:text-white transition"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={2.5} />
        {unread > 0 && (
          <span
            className="absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1 bg-[#FF4D00] text-white text-[10px] font-bold border-2 border-[#0A0A0B] flex items-center justify-center animate-pulse"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-w-[92vw] border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#FF4D00] z-50"
          data-testid="notification-drawer"
        >
          <div className="flex items-center justify-between border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white px-4 py-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#FF4D00]" strokeWidth={2.5} />
              <div className="mono text-[11px] uppercase tracking-widest font-semibold">
                Notifications {unread > 0 && `· ${unread} non lues`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {isPushSupported() && (
                <button
                  onClick={togglePush}
                  disabled={pushBusy}
                  className={`mono text-[10px] uppercase tracking-widest flex items-center gap-1 px-2 py-1 border ${
                    pushEnabled
                      ? "border-[#FF4D00] text-[#FF4D00] bg-white"
                      : "border-white/40 text-white hover:border-[#FF4D00] hover:text-[#FF4D00]"
                  }`}
                  data-testid="notification-push-toggle"
                  title={pushEnabled ? "Push activées — cliquer pour désactiver" : "Activer notifications navigateur"}
                >
                  {pushEnabled ? <><Zap className="w-3 h-3" /> Push</> : <><BellOff className="w-3 h-3" /> Off</>}
                </button>
              )}
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  className="text-[10px] mono uppercase tracking-widest text-[#FF4D00] hover:text-white flex items-center gap-1 px-2 py-1"
                  data-testid="notification-mark-all-read"
                  title="Marquer tout comme lu"
                >
                  <CheckCheck className="w-3 h-3" /> Tout lu
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-white hover:text-[#FF4D00] p-1"
                aria-label="Fermer"
                data-testid="notification-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {items.length > 0 && (
              <div className="flex gap-1 p-2 border-b-2 border-[#0A0A0B] bg-zinc-50 overflow-x-auto">
                {[
                  ["all", "Toutes"],
                  ["unread", "Non lues"],
                  ["message", "Messages"],
                  ["chantier", "Chantier"],
                  ["litige", "Litiges"],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border shrink-0 ${
                      filter === k
                        ? "bg-[#0A0A0B] text-white border-[#0A0A0B]"
                        : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#FF4D00]"
                    }`}
                    data-testid={`notif-filter-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
            {(() => {
              const filtered = items.filter((n) => {
                if (filter === "all") return true;
                if (filter === "unread") return !n.read;
                if (filter === "message") return n.event === "message.new";
                if (filter === "chantier") return ["quote.new","quote.accepted","escrow.paid","journal.updated","avenant.proposed","mission.validated"].includes(n.event);
                if (filter === "litige") return n.event.startsWith("dispute.");
                return true;
              });
              return filtered.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 mx-auto border-2 border-zinc-300 flex items-center justify-center mb-3">
                    <Bell className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="display text-base text-zinc-600">
                    {items.length === 0 ? "Aucune notification" : "Aucune notification dans ce filtre"}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Vous serez alerté ici en temps réel.</p>
                </div>
              ) : (
                <ul className="divide-y divide-zinc-200" data-testid="notification-list">
                  {filtered.map((n) => {
                  const isHigh = n.priority === "high";
                  return (
                    <li
                      key={n.id}
                      className={`p-4 flex gap-3 group ${n.read ? "bg-white" : "bg-[#FFF7ED]"}`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      {!n.read && (
                        <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isHigh ? "bg-[#FF4D00] animate-pulse" : "bg-[#FF4D00]"}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold leading-tight">{n.title}</div>
                        {n.body && (
                          <p className="text-xs text-zinc-600 mt-1 line-clamp-2 leading-snug">{n.body}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="mono text-[10px] text-zinc-400 uppercase tracking-wider">
                            {formatRelativeTime(n.created_at)}
                          </span>
                          {n.link && (
                            <Link
                              to={n.link}
                              onClick={() => {
                                if (!n.read) markOneRead(n.id);
                                setOpen(false);
                              }}
                              className="text-[10px] text-[#FF4D00] font-semibold uppercase tracking-widest mono hover:underline inline-flex items-center gap-1"
                              data-testid={`notification-link-${n.id}`}
                            >
                              Ouvrir <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!n.read && (
                          <button
                            onClick={() => markOneRead(n.id)}
                            className="text-zinc-400 hover:text-[#FF4D00] p-1"
                            title="Marquer lu"
                            data-testid={`notification-read-${n.id}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => removeNotification(n.id)}
                          className="text-zinc-400 hover:text-red-600 p-1"
                          title="Supprimer"
                          data-testid={`notification-delete-${n.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
                </ul>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
